import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useTechnicusFeedComparison } from "./useTechnicusFeedComparison";
import { useTechnicusFeedContent } from "./useTechnicusFeedContent";
import { useTechnicusFeedRegistration } from "./useTechnicusFeedRegistration";
import { createTechnicusCellRenderers } from "../../components/technicus/technicusCellRenderers";
import { registerJobSyncCellRenderersHook } from "../../hooks/cellRenderersHooks";
import { registerJobSyncIntegrationDataHook } from "../../hooks/integrationDataHooks";
import { registerJobSyncRowClickHook } from "../../hooks/rowClickHooks";
import { registerJobSyncTableDataHook } from "../../hooks/tableDataHooks";
import { createTechnicusRowClickHandler } from "../../utils/technicusRowClick";
import { jobSyncQueryKeys } from "../query-keys";

import { useDrawer } from "@/components/ui/v2/use-drawer";
import { registerModuleToggleHandler } from "@/features/integrations/hooks/moduleToggleHandlers";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import type { ModuleDataValue } from "@/features/integrations/shared/renderer/hooks/types";
import useToast from "@/lib/hooks/useToast";

// Clear Technicus job-sync cache when module is toggled so cards refetch fresh state
registerModuleToggleHandler("technicus", "job-sync", (queryClient) => {
  queryClient.invalidateQueries({
    queryKey: jobSyncQueryKeys.byModule("technicus"),
    refetchType: "all",
  });
});

/**
 * Register Technicus-specific hooks for job-sync module.
 * This file is imported at module initialization to register hooks.
 * 
 * Note: Registered functions are hooks themselves - they will be called
 * at component level by the generic hooks.
 */

function useTechnicusTableDataImpl(params: {
  clientId: string | undefined;
  tenantId: string | undefined;
  page: number;
  pageSize: number;
}) {
  const { clientId, page, pageSize } = params;
  const { t } = useTranslation();
  
  // Get feedUrl from feed registration to use in feed content query
  // This ensures the table shows what's actually in the feed URL
  const feedRegistrationQuery = useTechnicusFeedRegistration(clientId);
  const feedUrl = feedRegistrationQuery.data?.feedUrl ?? null;
  const queryResult = useTechnicusFeedContent(feedUrl);
  
  // Transform the result to match expected format using useMemo
  // Apply pagination by slicing the data array
  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;
    
    const allJobs = queryResult.data.jobs ?? [];
    const totalRecords = queryResult.data.totalJobs ?? 0;
    
    // Calculate pagination slice
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedJobs = allJobs.slice(startIndex, endIndex);
    
    return {
      data: paginatedJobs,
      totalRecords,
    };
  }, [queryResult.data, page, pageSize]);
  
  // Determine empty message based on feed registration and content query states
  const emptyMessage = useMemo(() => {
    // Don't show empty message while feed registration is still loading
    if (feedRegistrationQuery.isLoading) {
      return undefined;
    }
    
    // No feed URL registered (and not loading)
    if (!feedUrl && !feedRegistrationQuery.isLoading) {
      return t("integrations.technicus.jobSync.feedContent.empty.noFeedUrl");
    }
    
    // Feed URL error (network error, parsing error, etc.)
    if (queryResult.error) {
      const error = queryResult.error as { message?: string; response?: { status?: number } };
      // Check if it's a parsing/format error vs network error
      if (error.message?.includes("Feed URL is required") || error.message?.includes("parse")) {
        return t("integrations.technicus.jobSync.feedContent.empty.invalidFormat");
      }
      if (error.response?.status === 404) {
        return t("integrations.technicus.jobSync.feedContent.empty.feedNotFound");
      }
      return t("integrations.technicus.jobSync.feedContent.empty.feedError");
    }
    
    // Feed loaded successfully but no jobs
    if (queryResult.data && queryResult.data.totalJobs === 0) {
      return t("integrations.technicus.jobSync.feedContent.empty.noJobs");
    }
    
    return undefined;
  }, [feedRegistrationQuery.isLoading, feedUrl, queryResult.error, queryResult.data, t]);
  
  // Return transformed query result with emptyMessage as a custom property
  // TypeScript will complain but this works at runtime
  return {
    ...queryResult,
    data: transformedData,
    emptyMessage,
  } as unknown as UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError> & { emptyMessage?: string };
}

// Register table data hook for Technicus (feed content)
// Only applies to client detail view - tenant list uses useJobSyncClients instead
registerJobSyncTableDataHook("technicus", (clientId) =>
  clientId ? useTechnicusTableDataImpl : undefined);

// Register integration data hook for Technicus (feed registration)
// The registered function is a hook that will be called by the generic hook at component level
// This hook includes Technicus-specific error handling and data transformations
registerJobSyncIntegrationDataHook("technicus", function useTechnicusIntegrationData(params) {
  const { integrationName, clientId, tenantId } = params;
  
  const queryResult = useTechnicusFeedRegistration(clientId);
  const comparisonQueryResult = useTechnicusFeedComparison(clientId);
  
  // Preserve the last known comparison data to prevent data loss during refetches
  // React Query keeps previous data during refetches, but we need to handle the case
  // where data might be undefined during initial load or query reset
  const lastComparisonDataRef = useRef(comparisonQueryResult.data);
  
  // Update ref when comparison data is available
  // Clear ref when data becomes undefined AND query is loading (indicates reset)
  // This prevents stale ref data from being used after optimistic resets
  useEffect(() => {
    if (comparisonQueryResult.data) {
      lastComparisonDataRef.current = comparisonQueryResult.data;
    } else if (comparisonQueryResult.isLoading || comparisonQueryResult.isFetching) {
      // Clear ref when data is undefined and query is loading/fetching (reset scenario)
      // This ensures we don't use stale ref data after optimistic resets
      lastComparisonDataRef.current = undefined;
    }
  }, [comparisonQueryResult.data, comparisonQueryResult.isLoading, comparisonQueryResult.isFetching]);
  
  // Check if module is active (needed for failure detection)
  const moduleQuery = useIntegrationModule({
    integrationName,
    moduleName: "job-sync",
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });
  const isModuleActive = moduleQuery.data?.isActive ?? false;
  
  // Transform query result with Technicus-specific logic
  // This useMemo ensures we recompute when comparison data arrives
  const transformedData = useMemo(() => {
    // Get comparison data - use current data if available
    // Only fall back to preserved data if query is NOT loading/fetching (to avoid stale data during refetches)
    // When queries are reset optimistically, data becomes undefined and isLoading/isFetching becomes true
    // In that case, we should NOT use stale ref data - treat it as truly loading
    // Also check for loading placeholder (set during reregister to show seamless loading)
    const rawComparisonData = comparisonQueryResult.data;
    const isPlaceholder = rawComparisonData && typeof rawComparisonData === 'object' && '__isLoadingPlaceholder' in rawComparisonData;
    const comparisonData = isPlaceholder 
      ? undefined // Treat placeholder as loading (undefined)
      : (rawComparisonData ?? 
          (!comparisonQueryResult.isLoading && !comparisonQueryResult.isFetching 
            ? lastComparisonDataRef.current 
            : undefined));
    
    // Check if registration failed (422 status)
    const errorData = queryResult.error?.response?.data as { title?: string; detail?: string } | undefined;
    const isRegistrationFailed =
      queryResult.isError &&
      queryResult.error?.response?.status === 422 &&
      errorData?.title === "Technicus.FeedRegistration.Failed";
    
    const data = queryResult.data;
    
    // If registration failed (422 error), delay failure UI while comparison is in-flight,
    // and prefer comparison data once it arrives (schema-based rendering expects data fill-in).
    if (isRegistrationFailed) {
      if (comparisonQueryResult.isLoading || comparisonQueryResult.isFetching) {
        return {
          feedStatus: "Loading",
          feedStatusLabel: "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.loading",
          loadingTitleKey: "integrations.technicus.jobSync.feedRegistration.checkingTechnicus",
          loadingDescriptionKey: "integrations.technicus.jobSync.feedRegistration.checkingTechnicusDescription",
          clientId: clientId ?? undefined,
          isLoading: true,
        };
      }

      // Note: With feedId-only matching, we no longer allow saving registrations found in Technicus
      // If we can't match on feedId, it means the registration wasn't created by us
      // This code path is no longer reachable but kept for clarity

      return {
        feedId: comparisonData?.technicusFeedId ?? "",
        feedUrl: comparisonData?.technicusFeedUrl ?? "",
        feedStatus: "Failed",
        feedStatusLabel: "integrations.technicus.jobSync.feedRegistration.status.label.failed",
        registrationDate: null,
        clientId: clientId ?? undefined,
        // Don't set retryAction here - InformationCardComponent failure state will use failureRetryAction instead
        // This prevents the top-right button from showing (which would duplicate the card buttons)
        failureRetryAction: "reregisterFeed",
        failureTitleKey: "integrations.technicus.jobSync.feedRegistration.registrationFailed",
        failureDescriptionKey: "integrations.technicus.jobSync.feedRegistration.registrationFailedDescription",
        failureReason: errorData?.detail ?? undefined, // Extract detail field for error message
        actionLabelKey: "integrations.technicus.jobSync.feedRegistration.reregister",
        successMessageKey: "integrations.technicus.jobSync.feedRegistration.reregisterSuccess",
        errorMessageKey: "integrations.technicus.jobSync.feedRegistration.reregisterError",
        requiresClientId: true,
        // Include Technicus comparison data even when registration failed
        technicusFeedId: comparisonData?.technicusFeedId ?? null,
        technicusFeedUrl: comparisonData?.technicusFeedUrl ?? null,
        isMismatched: comparisonData?.isMismatched ?? false,
        isFeedUrlMismatched: comparisonData?.isFeedUrlMismatched ?? false,
      };
    }
    
    // If no registration data exists, check comparison state first
    if (!data) {
      
      // If comparison is still loading or fetching, return loading state for schema-based rendering
      // This must be checked BEFORE the "Failed" check to prevent showing error prematurely
      // Use isFetching OR isLoading because isLoading is only true on initial load,
      // but isFetching is true during refetches as well
      if (comparisonQueryResult.isLoading || comparisonQueryResult.isFetching) {
        return {
          feedStatus: "Loading",
          feedStatusLabel: "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.loading",
          loadingTitleKey: "integrations.technicus.jobSync.feedRegistration.checkingTechnicus",
          loadingDescriptionKey: "integrations.technicus.jobSync.feedRegistration.checkingTechnicusDescription",
          clientId: clientId ?? undefined,
          isLoading: true,
        };
      }
      
      // If query completed successfully but no registration exists AND module is active, it means registration failed
      // BUT only if comparison is NOT loading (checked above)
      if (
        !queryResult.isLoading &&
        !queryResult.isError &&
        isModuleActive
      ) {
        return {
          feedId: comparisonData?.technicusFeedId ?? "",
          feedUrl: comparisonData?.technicusFeedUrl ?? "",
          feedStatus: "Failed",
          feedStatusLabel: "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.failed",
          registrationDate: null,
          clientId: clientId ?? undefined,
          // Don't set retryAction here - InformationCardComponent failure state will use failureRetryAction instead
          // This prevents the top-right button from showing (which would duplicate the card buttons)
          failureRetryAction: "reregisterFeed",
          failureTitleKey: "integrations.technicus.jobSync.feedRegistration.registrationFailed",
          failureDescriptionKey: "integrations.technicus.jobSync.feedRegistration.registrationFailedDescription",
          failureReason: undefined, // No error detail available in this case
          actionLabelKey: "integrations.technicus.jobSync.feedRegistration.reregister",
          successMessageKey: "integrations.technicus.jobSync.feedRegistration.reregisterSuccess",
          errorMessageKey: "integrations.technicus.jobSync.feedRegistration.reregisterError",
          requiresClientId: true,
          // Include Technicus comparison data even when registration failed
          technicusFeedId: comparisonData?.technicusFeedId ?? null,
          technicusFeedUrl: comparisonData?.technicusFeedUrl ?? null,
          isMismatched: comparisonData?.isMismatched ?? false,
          isFeedUrlMismatched: comparisonData?.isFeedUrlMismatched ?? false,
        };
      }
      
      return undefined;
    }
    
    // Transform the data to a common format
    const feedId = data.feedId;
    const feedUrl = data.feedUrl;
    const createdDate = data.createdDate;
    
    // hasMismatch should include both isMismatched and isFeedUrlMismatched
    const isMismatched = comparisonData?.isMismatched ?? false;
    const isFeedUrlMismatched = comparisonData?.isFeedUrlMismatched ?? false;
    const hasMismatch = isMismatched || isFeedUrlMismatched;
    
    // Check if we have a registration but no matching feed in Technicus (feedId not found)
    // This means the registration wasn't created by us
    // Only treat as "not found" if comparison query completed successfully (not loading, not error, has data)
    const hasRegistrationButNoMatch = feedId && 
                                      !comparisonQueryResult.isLoading && 
                                      !comparisonQueryResult.isFetching &&
                                      !comparisonQueryResult.isError &&
                                      comparisonData !== undefined &&
                                      (comparisonData.technicusFeedId === null || comparisonData.technicusFeedId === undefined);
    
    // Build detailed mismatch message for tooltip
    // Combine multiple mismatch types into a single message
    let mismatchDetailText = "integrations.technicus.jobSync.feedRegistration.status.mismatch";
    if (hasMismatch) {
      const mismatchParts: string[] = [];
      if (isFeedUrlMismatched) {
        mismatchParts.push("integrations.technicus.jobSync.feedRegistration.feedUrlMismatch");
      }
      // If we have specific mismatch details, use them; otherwise use generic message
      if (mismatchParts.length > 0) {
        // Store mismatch details as an array for TextComponent to handle
        mismatchDetailText = mismatchParts.join("|");
      }
    }
    
    const feedStatusText = hasMismatch
      ? mismatchDetailText
      : hasRegistrationButNoMatch
        ? "integrations.technicus.jobSync.feedRegistration.status.notFoundInTechnicus"
        : feedId
          ? "integrations.technicus.jobSync.feedRegistration.registered"
          : "integrations.technicus.jobSync.feedRegistration.notRegistered";
    const feedStatusLabel = hasMismatch
      ? "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.mismatch"
      : hasRegistrationButNoMatch
        ? "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.notFound"
        : feedId
          ? "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.registered"
          : "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.notRegistered";
    const feedStatusVariant = hasMismatch || hasRegistrationButNoMatch
      ? "Warning"
      : feedId
        ? "Success"
        : "Default";
    
    // Check if Technicus comparison is still loading
    // Also check for loading placeholder (set during reregister to show seamless loading)
    // Reuse isPlaceholder from above - it's already computed from rawComparisonData
    const isTechnicusLoading = isPlaceholder || comparisonQueryResult.isLoading || comparisonQueryResult.isFetching;
    
    const technicusStatusText = isTechnicusLoading
      ? "Loading"
      : comparisonData
        ? hasMismatch
          ? mismatchDetailText
          : hasRegistrationButNoMatch
            ? "integrations.technicus.jobSync.feedRegistration.status.notFoundInTechnicus"
            : "integrations.technicus.jobSync.feedRegistration.registered"
        : undefined;
    const technicusStatusLabel = isTechnicusLoading
      ? "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.loading"
      : comparisonData
        ? hasMismatch
          ? "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.mismatch"
          : hasRegistrationButNoMatch
            ? "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.notFound"
            : "integrations.technicus.jobSync.feedRegistration.status.badgeLabel.registered"
        : undefined;
    const technicusStatusVariant = comparisonData
      ? hasMismatch || hasRegistrationButNoMatch
        ? "Warning"
        : "Success"
      : undefined;

    // Show reregister button for all non-success cases (mismatch, not found, failed, etc.)
    // BUT only when we have a registration (feedId exists) - can't reregister if there's nothing to reregister
    // feedStatusVariant is "Success" only when everything is good, so show button for anything else
    const shouldShowReregister = feedStatusVariant !== "Success" && feedId;

    return {
      ...data,
      feedId: feedId ?? "",
      feedUrl: feedUrl ?? "",
      feedStatus: feedStatusText,
      feedStatusLabel,
      feedStatusVariant,
      technicusStatus: technicusStatusText,
      technicusStatusLabel,
      technicusStatusVariant,
      // Add loading message keys when Technicus status is loading
      // These are used by InformationCardComponent to show full loading state for Technicus card
      loadingMessageKey: isTechnicusLoading
        ? "integrations.technicus.jobSync.feedRegistration.checkingTechnicus"
        : undefined,
      loadingTitleKey: isTechnicusLoading
        ? "integrations.technicus.jobSync.feedRegistration.checkingTechnicus"
        : undefined,
      loadingDescriptionKey: isTechnicusLoading
        ? "integrations.technicus.jobSync.feedRegistration.checkingTechnicusDescription"
        : undefined,
      noLocalRegistrationNote: "",
      registrationDate: createdDate ?? null,
      clientId: clientId ?? undefined,
      retryAction: shouldShowReregister ? "reregisterFeed" : undefined,
      actionLabelKey: shouldShowReregister ? "integrations.technicus.jobSync.feedRegistration.reregister" : undefined,
      successMessageKey: shouldShowReregister ? "integrations.technicus.jobSync.feedRegistration.reregisterSuccess" : undefined,
      errorMessageKey: shouldShowReregister ? "integrations.technicus.jobSync.feedRegistration.reregisterError" : undefined,
      requiresClientId: shouldShowReregister ? true : undefined,
      // Include Technicus comparison data for schema-based rendering
      // Only include Technicus data if comparison is done (not loading)
      technicusFeedId: isTechnicusLoading ? null : (comparisonData?.technicusFeedId ?? null),
      technicusFeedUrl: isTechnicusLoading ? null : (comparisonData?.technicusFeedUrl ?? null),
      isMismatched: comparisonData?.isMismatched ?? false,
      isFeedUrlMismatched: comparisonData?.isFeedUrlMismatched ?? false,
    };
  }, [
    queryResult.data,
    queryResult.error,
    queryResult.isError,
    queryResult.isLoading,
    isModuleActive,
    clientId,
    comparisonQueryResult.data,
    comparisonQueryResult.isLoading,
    comparisonQueryResult.isFetching,
    comparisonQueryResult.isError,
  ]);
  
  // Return transformed query result with proper typing
  // Use useMemo to ensure a new object reference when transformedData or queryResult changes
  // This is critical for React Query to detect changes and trigger re-renders
  // When comparison data arrives, transformedData changes, creating a new queryResult.data reference
  const result = useMemo(() => {
    return {
      ...queryResult,
      data: transformedData,
    } as unknown as UseQueryResult<Record<string, ModuleDataValue>, AxiosError>;
  }, [queryResult, transformedData]);
  
  return result;
});

// Register cell renderers hook for Technicus
// The registered function is a hook that will be called by the generic hook at component level
registerJobSyncCellRenderersHook("technicus", function useTechnicusCellRenderers(params) {
  const { clientId } = params;
  const { openDrawer } = useDrawer();
  const { t } = useTranslation();
  const { showToast } = useToast();

  if (!clientId) return undefined;

  const onTitleClick = createTechnicusRowClickHandler(clientId, openDrawer, {
    drawerTitle: t("vacancies.drawer.title", { defaultValue: "Vacancy details" }),
    onError: () => showToast(t("common.error", { defaultValue: "Something went wrong" }), "error"),
  });

  return createTechnicusCellRenderers(onTitleClick);
});

// Register row click hook for Technicus - return undefined to disable row-level clicks
// Only the title cell is clickable now
registerJobSyncRowClickHook("technicus", function useTechnicusRowClick() {
  // Return undefined to disable row-level clicking
  // Title cell handles clicks via cell renderer
  return undefined;
});

// Export hooks for direct use if needed
export { useTechnicusFeedContent } from "./useTechnicusFeedContent";
export { useTechnicusFeedRegistration } from "./useTechnicusFeedRegistration";
export { useReregisterTechnicusFeed } from "./useReregisterTechnicusFeed";
export type {
  TechnicusFeedRegistrationDto,
  TechnicusFeedContentDto,
  TechnicusFeedJobDto,
} from "./types";
